import { Alert, Button, Group, Paper, SimpleGrid, Stack, Stepper, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { StatusBadge } from "../../components/StatusBadge";
import { JobAdditionalDetails } from "../../components/jobs/JobAdditionalDetails";
import { ConfirmDialog, LoadingOverlay } from "../../components/ui";
import { jobsApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { formatCities, formatPaise, SEGMENT_LABELS, STAGE_TYPE_LABELS } from "../../lib/format";

type JobLifecycleAction = "pause" | "close";

const JOB_LIFECYCLE_CONFIRMATION: Record<
  JobLifecycleAction,
  { title: string; description: string; confirmLabel: string }
> = {
  pause: {
    title: "Pause this job?",
    description: "Recruiters will no longer see this job or submit new candidates. You can resume it later by re-submitting it for review.",
    confirmLabel: "Pause job",
  },
  close: {
    title: "Close this job?",
    description: "This permanently closes the job to new candidates. Existing submissions and hiring records remain available, but the job cannot be reopened.",
    confirmLabel: "Close job",
  },
};

export function HrJobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pendingLifecycleAction, setPendingLifecycleAction] = useState<JobLifecycleAction | null>(null);

  const jobQuery = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => jobsApi.get(jobId!),
    enabled: !!jobId,
  });

  const actionMutation = useMutation({
    mutationFn: (action: "publish" | "pause" | "close") => {
      if (action === "publish") return jobsApi.publish(jobId!);
      if (action === "pause") return jobsApi.pause(jobId!);
      return jobsApi.close(jobId!);
    },
    onSuccess: (_, action) => {
      setPendingLifecycleAction(null);
      const message =
        action === "publish"
          ? "Job submitted for AxioHire fee review. It will open once approved."
          : `Job ${action}d successfully.`;
      notifications.show({ color: "green", title: "Updated", message });
      queryClient.invalidateQueries({ queryKey: ["job", jobId] });
      queryClient.invalidateQueries({ queryKey: ["hr-jobs"] });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  if (jobQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-300)" />;
  }

  const job = jobQuery.data;
  if (!job) return <Text c="dimmed">Job not found.</Text>;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Group gap="xs" mb="xs">
            <StatusBadge status={job.status} />
            <Text size="sm" c="dimmed">
              {SEGMENT_LABELS[job.segment]}
            </Text>
          </Group>
          <Title order={2}>{job.title}</Title>
          <Text c="dimmed">
            {formatCities(job.cities)}
            {job.is_remote ? " · Remote OK" : ""} · {job.openings_filled}/{job.openings_total} filled
          </Text>
        </div>
        <Group>
          {(job.status === "draft" || job.status === "paused") && (
            <Button variant="default" component={Link} to={`/hr/jobs/${job.id}/edit`}>
              Edit
            </Button>
          )}
          {job.status === "draft" && (
            <Button color="violet" loading={actionMutation.isPending} onClick={() => actionMutation.mutate("publish")}>
              Submit for review
            </Button>
          )}
          {job.status === "open" && (
            <Button variant="default" loading={actionMutation.isPending} onClick={() => setPendingLifecycleAction("pause")}>
              Pause
            </Button>
          )}
          {job.status === "paused" && (
            <Button color="violet" loading={actionMutation.isPending} onClick={() => actionMutation.mutate("publish")}>
              Re-submit for review
            </Button>
          )}
          {(job.status === "open" || job.status === "paused") && (
            <Button color="red" variant="light" loading={actionMutation.isPending} onClick={() => setPendingLifecycleAction("close")}>
              Close
            </Button>
          )}
        </Group>
      </Group>

      {job.status === "pending_review" && (
        <Alert color="orange" variant="light" title="Pending AxioHire fee review">
          This job is awaiting fee split approval. Recruiters will see it once AxioHire confirms the
          three-way fee split.
        </Alert>
      )}

      {pendingLifecycleAction !== null && (
        <ConfirmDialog
          opened
          onClose={() => setPendingLifecycleAction(null)}
          title={JOB_LIFECYCLE_CONFIRMATION[pendingLifecycleAction].title}
          description={JOB_LIFECYCLE_CONFIRMATION[pendingLifecycleAction].description}
          confirmLabel={JOB_LIFECYCLE_CONFIRMATION[pendingLifecycleAction].confirmLabel}
          loading={actionMutation.isPending}
          onConfirm={() => actionMutation.mutate(pendingLifecycleAction)}
        />
      )}

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Paper withBorder p="md">
          <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
            CTC band
          </Text>
          <Text fw="var(--axio-font-weight-semibold)" mt="var(--axio-unit-4)" className="tabular">
            {formatPaise(job.ctc_min_paise)} – {formatPaise(job.ctc_max_paise)}
          </Text>
        </Paper>
        <Paper withBorder p="md">
          <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
            Fee per hire
          </Text>
          <Text mt="var(--axio-unit-4)" className="moneyAmount">
            {job.fee_basis === "flat" ? formatPaise(job.fee_flat_paise) : `${job.fee_pct_ctc}% of CTC`}
          </Text>
        </Paper>
        <Paper withBorder p="md">
          <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
            Guarantee
          </Text>
          <Text fw="var(--axio-font-weight-semibold)" mt="var(--axio-unit-4)" className="tabular">
            {job.guarantee_days} days
          </Text>
        </Paper>
      </SimpleGrid>

      <JobAdditionalDetails job={job} audience="hr" />

      {job.description && (
        <Paper withBorder p="md">
          <Title order={5} mb="xs">
            Description
          </Title>
          <Text style={{ whiteSpace: "pre-wrap" }}>{job.description}</Text>
        </Paper>
      )}

      {job.requirements && (
        <Paper withBorder p="md">
          <Title order={5} mb="xs">
            Requirements
          </Title>
          <Text style={{ whiteSpace: "pre-wrap" }}>{job.requirements}</Text>
        </Paper>
      )}

      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Hiring Flow
        </Title>
        <Stepper active={-1} iconSize={32} allowNextStepsSelect={false}>
          {job.stages.map((stage) => (
            <Stepper.Step
              key={stage.id}
              label={stage.name}
              description={STAGE_TYPE_LABELS[stage.stage_type] ?? stage.stage_type}
            />
          ))}
        </Stepper>
      </Paper>

      <Button variant="subtle" onClick={() => navigate("/hr/jobs")}>
        ← Back to jobs
      </Button>
    </Stack>
  );
}
